/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: MOHFormView.tsx
 🗓️ Created: 2025-01-XX
 🧩 Description:
    Music on Hold (MOH) Sources form component with file upload support.
    Supports creating and editing MOH sources for FreeSWITCH domains.

 🔧 Notes:
    - Implements file upload for local MOH files
    - Supports stream URLs and chime configurations
    - Handles domain-based MOH organization
    - Provides comprehensive validation and error handling

 🔁 Last Updated: 2025-01-XX by Rodrigo Cuadra
────────────────────────────────────────────────────────────────*/

import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Music, ArrowLeft, Upload, X, Play, Pause, Volume2, GripVertical, Plus, Trash2 } from 'lucide-react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import { useNotifications } from '../../contexts/NotificationContext';
import { useI18n } from '../../hooks/useI18n';
import { buildApiUrl, getApiHeaders } from '../../config/api';
import { FormBox } from '../../components/ui/FormBox';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import Select from '../../components/ui/select';
import Toggle from '../../components/ui/Toggle';
import { SimpleTooltip } from '../../components/ui/SimpleTooltip';
import { ModuleFormHeader } from '../../components/layouts';
import { FixedActionBar } from '../../components/ui/FixedActionBar';
import { useSaveWithAction } from '../../hooks/useSaveWithAction';
import ConfirmationModal from '../../components/ui/ConfirmationModal';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import { MOHSource, MOHSourceFormData, FormSnapshot } from './types';
import { SortableFileItem } from './components';
import { MOHSourceConfig } from './components/MOHSourceConfig';
import { useFileUpload, useAudioPreview } from './hooks';
import { generateClassName } from './utils';

// Global Set to track shown delete notifications across component mounts/unmounts
// This prevents duplicate notifications when navigation causes re-renders
const deleteNotificationsShown = new Set<string>();

export default function MOHFormView() {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { t } = useI18n();
  const { showSuccess, showError, showInfo } = useNotifications();

  // Use refs for notification functions to avoid closure issues and prevent duplicates
  const showSuccessRef = useRef(showSuccess);
  const showErrorRef = useRef(showError);
  const showInfoRef = useRef(showInfo);

  useEffect(() => {
    showSuccessRef.current = showSuccess;
    showErrorRef.current = showError;
    showInfoRef.current = showInfo;
  }, [showSuccess, showError, showInfo]);

  // Drag & Drop sensors
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // State management
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isDefault, setIsDefault] = useState(false);

  // Form data
  const [formData, setFormData] = useState<MOHSourceFormData>({
    name: '',
    class_name: 'default',
    source_type: 'file',
    source_path: '',
    description: '',
    enabled: true,
    priority: 0,
    play_mode: 'linear', // How to play multiple files: linear, random, shuffle
    interval: 20,
    chime_enabled: false,
    chime_freq: 90,
    file: null
  });

  // Use file upload hook
  const fileUpload = useFileUpload();

  // Use audio preview hook
  const audioPreview = useAudioPreview();

  // Tabs state
  const [activeFileTab, setActiveFileTab] = useState<'music' | 'chime'>('music');

  // Refs for form submission tracking
  const isPausingManuallyRef = useRef(false); // Track if we're manually pausing to avoid error on pause
  const isSubmittingRef = useRef(false);
  const isDeletingRef = useRef(false); // Track if delete is in progress to prevent duplicates
  const initialStateRef = useRef<FormSnapshot | null>(null);
  const lastSaveTimestamp = useRef(0); // Track last save timestamp to prevent duplicate submissions and notifications
  const lastDeleteTimestamp = useRef(0); // Track last delete timestamp separately to prevent duplicate delete notifications
  const deleteOperationIdRef = useRef<string | null>(null); // Unique ID for each delete operation to prevent duplicates
  const lastIdRef = useRef<string | undefined>(id);
  const lastResetParamRef = useRef<string | null>(null);

  const buildCurrentSnapshot = useCallback((): FormSnapshot => {
    const { file: _ignoredFile, ...formWithoutFile } = formData;

    return {
      form: formWithoutFile,
      existingFiles: fileUpload.existingFiles.map((file) => file.name || file.path),
      newFiles: fileUpload.selectedFiles.map((file) => file.name),
      existingChimeFiles: fileUpload.existingChimeFiles.map((file) => file.name || file.path),
      newChimeFiles: fileUpload.selectedChimeFiles.map((file) => file.name),
    };
  }, [formData, fileUpload.existingFiles, fileUpload.existingChimeFiles, fileUpload.selectedFiles, fileUpload.selectedChimeFiles]);

  // Calculate hasChanges using useMemo for proper reactivity
  const hasChanges = useMemo(() => {
    const initialSnapshot = initialStateRef.current;
    if (!initialSnapshot) {
      // In create mode, we have changes if we've filled in any data or uploaded files
      return fileUpload.selectedFiles.length > 0 || fileUpload.selectedChimeFiles.length > 0 ||
        (formData.name.trim() !== '' || formData.source_type !== 'file' || formData.enabled !== true);
    }

    const currentSnapshot = buildCurrentSnapshot();
    return JSON.stringify(currentSnapshot) !== JSON.stringify(initialSnapshot);
  }, [formData, fileUpload.selectedFiles, fileUpload.selectedChimeFiles, fileUpload.existingFiles, fileUpload.existingChimeFiles, buildCurrentSnapshot]);

  // Load MOH source data for edit mode
  const loadMOHSource = useCallback(async (mohId: number | string) => {
    try {
      setIsLoading(true);
      const response = await fetch(
        buildApiUrl(`/api/telephony/moh-sources/${mohId}`),
        {
          method: 'GET',
          headers: getApiHeaders(),
        }
      );

      if (response.ok) {
        const result = await response.json();
        const moh = result.data;
        setIsDefault(!!moh.is_default);

        setFormData({
          name: moh.name || '',
          class_name: moh.class_name || 'default',
          source_type: moh.source_type || 'file',
          source_path: moh.source_path || '',
          description: moh.description || '',
          enabled: moh.enabled !== false,
          priority: moh.priority || 0,
          play_mode: moh.config?.settings?.play_mode || 'linear',
          interval: moh.interval || 20,
          chime_enabled: moh.config?.chime?.enabled || false,
          chime_freq: moh.config?.chime?.freq || 90,
          file: null
        });

        // Load existing chime files from config if available
        let chimeFileNames: string[] = [];
        if (moh.config?.chime?.list && Array.isArray(moh.config.chime.list)) {
          chimeFileNames = moh.config.chime.list;
          // Get chime file sizes from server
          const chimeFilesWithSizes = await Promise.all(
            moh.config.chime.list.map(async (fileName: string) => {
              try {
                const headUrl = buildApiUrl(`/api/telephony/moh-sources/${mohId}/file/${encodeURIComponent(fileName)}`);
                const headResponse = await fetch(headUrl, {
                  method: 'HEAD',
                  headers: getApiHeaders(),
                });

                if (headResponse.ok) {
                  const contentLength = headResponse.headers.get('Content-Length');
                  if (contentLength && contentLength !== '0' && contentLength !== 'null') {
                    const size = parseInt(contentLength, 10);
                    if (!isNaN(size) && size > 0) {
                      return {
                        name: fileName,
                        path: fileName,
                        size
                      };
                    }
                  }
                }
              } catch (e) {
              }

              return {
                name: fileName,
                path: fileName
              };
            })
          );

          fileUpload.setExistingChimeFiles(chimeFilesWithSizes);
        } else {
          fileUpload.setExistingChimeFiles([]);
        }

        // Load existing files from config if available (EXCLUDE chime files)
        if (moh.source_type === 'file' && moh.config && moh.config.files && Array.isArray(moh.config.files)) {
          // Filter out chime files from music files list
          const musicFilesOnly = moh.config.files.filter((file: { name: string; path: string }) => {
            const fileName = file.name || file.path;
            const isChimeFile = chimeFileNames.includes(fileName);
            return !isChimeFile;
          });

          // Get file sizes from server
          const filesWithSizes = await Promise.all(
            musicFilesOnly.map(async (file: { name: string; path: string; size?: number }) => {
              // If file already has size, use it
              if (file.size && file.size > 0) {
                return file;
              }

              // Try to get file size from server using HEAD request
              try {
                const headUrl = buildApiUrl(`/api/telephony/moh-sources/${mohId}/file/${encodeURIComponent(file.name)}`);

                const headResponse = await fetch(headUrl, {
                  method: 'HEAD',
                  headers: getApiHeaders(),
                });


                if (headResponse.ok) {
                  const contentLength = headResponse.headers.get('Content-Length');

                  if (contentLength && contentLength !== '0' && contentLength !== 'null') {
                    const size = parseInt(contentLength, 10);
                    if (!isNaN(size) && size > 0) {
                      return {
                        ...file,
                        size
                      };
                    } else {
                    }
                  } else {
                  }
                } else {
                }
              } catch (e) {
              }

              // Return file without size if HEAD request failed
              return file;
            })
          );

          fileUpload.setExistingFiles(filesWithSizes);
        } else if (moh.source_type === 'file' && moh.source_path) {
          // Fallback: parse from source_path (comma-separated)
          const files = moh.source_path.split(',').map((f: string) => ({
            name: f.trim(),
            path: f.trim()
          }));
          fileUpload.setExistingFiles(files);
        } else {
          fileUpload.setExistingFiles([]);
        }

        // Update initial snapshot after successful save
        setTimeout(() => {
          initialStateRef.current = buildCurrentSnapshot();
        }, 0);
      } else {
        showError(t('mohSources.errors.loadFailed') || 'Failed to load MOH source');
        navigate('/settings/voice-prompts/music-hold');
      }
    } catch (error: any) {
      showError(error.message || t('mohSources.errors.loadFailed') || 'Failed to load MOH source');
      navigate('/settings/voice-prompts/music-hold');
    } finally {
      setIsLoading(false);
    }
  }, [showError, t, navigate, buildCurrentSnapshot]);

  // Persist initial snapshot for "no changes" detection (creation flow)
  useEffect(() => {
    if (!initialStateRef.current && !isEditMode && !isLoading) {
      initialStateRef.current = buildCurrentSnapshot();
    }
  }, [buildCurrentSnapshot, isEditMode, isLoading]);

  // Load data on mount - only run once when id changes
  const loadedIdRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    if (id && id !== 'new' && loadedIdRef.current !== id) {
      loadedIdRef.current = id;
      setIsEditMode(true);
      loadMOHSource(id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]); // Only depend on id, not on loadMOHSource to prevent loop

  // Reset form when navigating to 'new' after Save and New
  useEffect(() => {
    const currentId = id || 'new';
    const previousId = lastIdRef.current;
    const currentResetParam = searchParams.get('_reset');

    // Reset when:
    // 1. We're navigating to 'new' (id is 'new' or undefined) AND
    // 2. Either the ID changed from a number to 'new', OR there's a _reset param that changed
    const isNavigatingToNew = (!id || id === 'new');
    const idChangedToNew = previousId && previousId !== 'new' && currentId === 'new';
    const resetParamChanged = currentResetParam && currentResetParam !== lastResetParamRef.current;

    const shouldReset = isNavigatingToNew && (idChangedToNew || resetParamChanged);

    if (shouldReset) {
      setFormData({
        name: '',
        class_name: 'default',
        source_type: 'file',
        source_path: '',
        description: '',
        enabled: true,
        priority: 0,
        play_mode: 'linear',
        interval: 20,
        chime_enabled: false,
        chime_freq: 90,
        file: null
      });
      fileUpload.setSelectedFiles([]);
      fileUpload.setSelectedChimeFiles([]);
      fileUpload.setExistingFiles([]);
      fileUpload.setExistingChimeFiles([]);
      initialStateRef.current = null;
      setIsEditMode(false);

      if (currentResetParam) {
        const newSearchParams = new URLSearchParams(searchParams);
        newSearchParams.delete('_reset');
        const newSearch = newSearchParams.toString();
        window.history.replaceState({}, '', `${window.location.pathname}${newSearch ? `?${newSearch}` : ''}`);
      }
    }

    lastIdRef.current = currentId;
    lastResetParamRef.current = currentResetParam;
  }, [id, searchParams, fileUpload]);

  // File upload handlers - use hook handlers
  const handleFileInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      fileUpload.handleFileSelect(e.target.files);
      setFormData(prev => ({ ...prev, file: e.target.files![0] })); // Keep first file for backward compatibility
    }
  }, [fileUpload]);

  // Handle drag end for file reordering (only when play_mode === 'linear')
  const handleDragEnd = useCallback((event: DragEndEvent) => {
    const { active, over } = event;

    if (!over || active.id === over.id) {
      return;
    }

    // Only allow reordering when play_mode is 'linear'
    if (formData.play_mode !== 'linear') {
      return;
    }

    const activeId = String(active.id);
    const overId = String(over.id);

    // Check if dragging existing files
    if (activeId.startsWith('existing-') && overId.startsWith('existing-')) {
      fileUpload.setExistingFiles((items) => {
        const oldIndex = parseInt(activeId.replace('existing-', ''));
        const newIndex = parseInt(overId.replace('existing-', ''));
        return arrayMove(items, oldIndex, newIndex);
      });
      return;
    }

    // Check if dragging new files
    if (activeId.startsWith('file-') && overId.startsWith('file-')) {
      const oldIndex = parseInt(activeId.replace('file-', ''));
      const newIndex = parseInt(overId.replace('file-', ''));
      fileUpload.setSelectedFiles((items) => arrayMove(items, oldIndex, newIndex));
      return;
    }

    // Handle cross-drag between existing and new files
    // If dragging existing to new or vice versa, combine the arrays temporarily
    if (activeId.startsWith('existing-') || overId.startsWith('existing-')) {
      // For now, only allow reordering within same type
      // Could implement cross-type reordering if needed
      return;
    }
  }, [formData.play_mode]);

  // Chime file input handler - use hook handler
  const handleChimeFileInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      fileUpload.handleChimeFileSelect(e.target.files);
      // Reset input so same file can be selected again
      if (e.target) {
        e.target.value = '';
      }
    }
  }, [fileUpload]);

  // Handle stream test/playback - use audio preview hook
  const handleTestStream = useCallback(() => {
    if (!formData.source_path) {
      showError(t('mohSources.errors.sourcePathRequired') || 'Stream URL is required');
      return;
    }
    audioPreview.testStream(formData.source_path);
  }, [formData.source_path, showError, t, audioPreview]);

  // Remove file - use hook handler
  const handleRemoveFile = useCallback((fileName: string) => {
    fileUpload.removeFile(fileName);
    setFormData(prev => ({ ...prev, file: fileUpload.selectedFiles[0] || null }));

    // If removing the currently playing file, stop playback
    const index = fileUpload.selectedFiles.findIndex(f => f.name === fileName);
    if (index === audioPreview.playingFileIndex) {
      // Audio preview hook will handle cleanup
    }
  }, [fileUpload, audioPreview]);

  // Audio playback is now handled by useAudioPreview hook

  // Audio playback is now handled by useAudioPreview hook
  // Cleanup is handled by the hooks automatically

  // Handle input changes
  const handleInputChange = useCallback((field: keyof MOHSourceFormData, value: any) => {
    setFormData(prev => {
      const newData = { ...prev, [field]: value };

      // Auto-generate class_name from name
      if (field === 'name') {
        newData.class_name = generateClassName(value);
      }

      return newData;
    });
  }, [generateClassName]);

  // Handle form submission
  const handleSubmit = useCallback(async (e?: React.FormEvent): Promise<MOHSource | undefined> => {
    e?.preventDefault();

    // Prevent duplicate submissions
    if (isSubmittingRef.current) {
      return undefined;
    }

    // Validation
    if (!formData.name.trim()) {
      showError(t('mohSources.errors.nameRequired') || 'Name is required');
      return undefined;
    }

    if (!formData.source_path && formData.source_type !== 'file') {
      showError(t('mohSources.errors.sourcePathRequired') || 'Source path is required');
      return undefined;
    }

    if (formData.source_type === 'file' && fileUpload.selectedFiles.length === 0 && !isEditMode) {
      showError(t('mohSources.errors.fileRequired') || 'At least one file is required for file sources');
      return undefined;
    }

    // Prevent duplicate submissions (React StrictMode issue)
    // Check both ref and timestamp to ensure we don't execute twice
    if (isSubmittingRef.current) {
      return undefined;
    }

    const now = Date.now();
    if (now - lastSaveTimestamp.current < 2000) {
      // Increased to 2 seconds to be more robust against React StrictMode
      return undefined;
    }

    // Set both ref and timestamp BEFORE any async operations
    lastSaveTimestamp.current = now;
    isSubmittingRef.current = true;
    setIsSaving(true);

    try {
      // Check if we're editing and have existing files but no new files
      const hasFilesToUpload = formData.source_type === 'file' && (fileUpload.selectedFiles.length > 0 || fileUpload.selectedChimeFiles.length > 0);

      if (hasFilesToUpload) {
        // Upload files via multipart/form-data (upload all files)
        const formDataToSend = new FormData();
        fileUpload.selectedFiles.forEach((file) => {
          formDataToSend.append('files', file);
        });
        // Add chime files
        fileUpload.selectedChimeFiles.forEach((file) => {
          formDataToSend.append('chime_files', file);
        });
        formDataToSend.append('name', formData.name);
        formDataToSend.append('class_name', formData.class_name);
        formDataToSend.append('source_type', formData.source_type);
        formDataToSend.append('description', formData.description || '');
        formDataToSend.append('enabled', formData.enabled.toString());
        formDataToSend.append('priority', formData.priority.toString());
        formDataToSend.append('interval', formData.interval.toString());
        formDataToSend.append('play_mode', formData.play_mode);
        formDataToSend.append('chime_enabled', (formData.chime_enabled || false).toString());
        formDataToSend.append('chime_freq', (formData.chime_freq || 90).toString());

        // CRITICAL: Preserve existing music files order when editing (even if no new music files)
        if (isEditMode && fileUpload.existingFiles.length > 0) {
          formDataToSend.append('existing_files_order', JSON.stringify(fileUpload.existingFiles.map(f => f.name)));
        }

        // Preserve existing chime files order when editing
        if (isEditMode && fileUpload.existingChimeFiles.length > 0) {
          formDataToSend.append('existing_chime_files_order', JSON.stringify(fileUpload.existingChimeFiles.map(f => f.name)));
        }

        const headers = getApiHeaders();
        delete (headers as any)['Content-Type']; // Let browser set multipart boundary

        const url = isEditMode
          ? buildApiUrl(`/api/telephony/moh-sources/${id}`)
          : buildApiUrl('/api/telephony/moh-sources');

        const response = await fetch(url, {
          method: isEditMode ? 'PUT' : 'POST',
          headers,
          body: formDataToSend
        });

        const responseText = await response.text();
        let result;
        try {
          result = JSON.parse(responseText);
        } catch (e) {
          throw new Error('Invalid response from server');
        }

        if (!response.ok) {
          const errorMsg = result?.error || result?.message || 'Failed to save MOH source';
          throw new Error(errorMsg);
        }

        // Success - show notification (lastSaveTimestamp and isSubmittingRef already prevent duplicates)
        const successMessage = isEditMode
          ? (t('mohSources.messages.updateSuccess') || 'MOH source updated successfully')
          : (t('mohSources.messages.createSuccess') || 'MOH source created successfully');
        showSuccessRef.current(successMessage);

        // Update initial snapshot after successful save
        setTimeout(() => {
          initialStateRef.current = buildCurrentSnapshot();
        }, 0);

        // Return saved data for useSaveWithAction (navigation is now handled by useSaveWithAction)
        // Return result.data if it exists, otherwise return the result itself (some APIs return data directly)
        const savedData = result.data || result;
        if (savedData?.id) {
          return savedData;
        }
        return undefined;
      } else {
        // JSON submission for stream/chime or edit without file change (but may have reordered existing files)
        const url = isEditMode
          ? buildApiUrl(`/api/telephony/moh-sources/${id}`)
          : buildApiUrl('/api/telephony/moh-sources');

        // Prepare body with all fields
        const requestBody: any = {
          name: formData.name,
          class_name: formData.class_name,
          source_type: formData.source_type,
          source_path: formData.source_path,
          description: formData.description,
          enabled: formData.enabled,
          priority: formData.priority,
          play_mode: formData.play_mode,
          interval: formData.interval,
          chime_enabled: formData.chime_enabled || false,
          chime_freq: formData.chime_freq || 90
        };

        // CRITICAL: Always preserve existing music files when editing (even if no changes to music)
        if (isEditMode && formData.source_type === 'file' && fileUpload.existingFiles.length > 0) {
          requestBody.existing_files_order = fileUpload.existingFiles.map(f => f.name);
        }

        // Add chime files list (combine existing and new)
        const allChimeFiles = [...fileUpload.existingChimeFiles.map(f => f.name), ...fileUpload.selectedChimeFiles.map(f => f.name)];
        if (allChimeFiles.length > 0) {
          requestBody.chime_files = allChimeFiles;
        }

        // Also send existing chime files order if editing
        if (isEditMode && fileUpload.existingChimeFiles.length > 0) {
          requestBody.existing_chime_files_order = fileUpload.existingChimeFiles.map(f => f.name);
        }

        const response = await fetch(url, {
          method: isEditMode ? 'PUT' : 'POST',
          headers: {
            ...getApiHeaders(),
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(requestBody)
        });

        const responseText = await response.text();
        let result;
        try {
          result = JSON.parse(responseText);
        } catch (e) {
          throw new Error('Invalid response from server');
        }

        if (!response.ok) {
          const errorMsg = result?.error || result?.message || 'Failed to save MOH source';
          throw new Error(errorMsg);
        }

        // Success - show notification (lastSaveTimestamp and isSubmittingRef already prevent duplicates)
        const successMessage = isEditMode
          ? (t('mohSources.messages.updateSuccess') || 'MOH source updated successfully')
          : (t('mohSources.messages.createSuccess') || 'MOH source created successfully');
        showSuccessRef.current(successMessage);

        // Update initial snapshot after successful save
        setTimeout(() => {
          initialStateRef.current = buildCurrentSnapshot();
        }, 0);

        // Return saved data for useSaveWithAction (navigation is now handled by useSaveWithAction)
        // Return result.data if it exists, otherwise return the result itself (some APIs return data directly)
        const savedData = result.data || result;
        if (savedData?.id) {
          return savedData;
        }
        return undefined;
      }
    } catch (error: any) {
      showErrorRef.current(error.message || t('mohSources.errors.saveFailed') || 'Failed to save MOH source');
      return undefined;
    } finally {
      setIsSaving(false);
      isSubmittingRef.current = false;
    }
  }, [formData, fileUpload.selectedFiles, fileUpload.existingFiles, fileUpload.selectedChimeFiles, fileUpload.existingChimeFiles, isEditMode, id, hasChanges, showSuccess, showError, showInfo, t, buildCurrentSnapshot, fileUpload]);

  // Enhanced save handler for useSaveWithAction
  const handleSaveInternal = useCallback(async () => {
    const savedData = await handleSubmit();

    // If no changes were detected, return current data to allow navigation
    if (!savedData && isEditMode && id) {
      // Return current data with ID for navigation purposes
      return { id: Number(id) } as MOHSource;
    }

    return savedData;
  }, [handleSubmit, isEditMode, id]);

  const handleSaveWithAction = useSaveWithAction({
    onSave: handleSaveInternal,
    listPath: '/settings/voice-prompts/music-hold',
    newPath: '/settings/voice-prompts/music-hold/new',
    mode: isEditMode ? 'edit' : 'create',
    getEditPath: (savedData) => {
      // MOH Sources use ID for navigation
      return `/settings/voice-prompts/music-hold/${savedData.id}`;
    },
    getFormData: () => ({
      formData
    }),
    storageKey: 'mohSources:saveAndNewData',
  });

  // Handle delete
  const handleDelete = useCallback(() => {
    setShowDeleteModal(true);
  }, []);

  // Handle delete confirmation - wrapped to prevent duplicate calls
  const handleDeleteConfirm = useCallback(async () => {
    if (!id || id === 'new') {
      return;
    }

    // CRITICAL: Prevent duplicate delete operations (React StrictMode issue)
    // Check ref FIRST before doing anything else
    if (isDeletingRef.current) {
      return;
    }

    const deleteNow = Date.now();
    // Use separate timestamp for delete to avoid conflicts with save
    // Check timestamp BEFORE setting refs to prevent race conditions
    if (deleteNow - lastDeleteTimestamp.current < 2000) {
      return;
    }

    // Generate unique operation ID for this delete operation ONLY if we don't have one
    // This ensures the same ID is used for the entire operation, even if callback is called twice
    if (!deleteOperationIdRef.current) {
      deleteOperationIdRef.current = `${id}-${deleteNow}-${Math.random().toString(36).substr(2, 9)}`;
    }
    const operationId = deleteOperationIdRef.current;

    // Set ALL refs and state BEFORE any async operations to prevent duplicate execution
    lastDeleteTimestamp.current = deleteNow;
    isDeletingRef.current = true;
    setIsDeleting(true);
    setShowDeleteModal(false); // Close modal immediately to prevent multiple clicks

    try {
      const response = await fetch(
        buildApiUrl(`/api/telephony/moh-sources/${id}`),
        {
          method: 'DELETE',
          headers: getApiHeaders()
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: 'Failed to delete MOH source' }));
        throw new Error(errorData.message || errorData.error || 'Failed to delete MOH source');
      }

      // Only show notification once per operation ID, even if React StrictMode causes re-execution
      // Also check if we've already shown this notification to prevent duplicates from navigation/re-renders
      // Use global Set to persist across component mounts/unmounts
      if (deleteOperationIdRef.current === operationId && !deleteNotificationsShown.has(operationId)) {
        const deleteMessage = t('mohSources.messages.deleteSuccess') || 'MOH source deleted successfully';

        // Mark notification as shown BEFORE showing it to prevent race conditions
        deleteNotificationsShown.add(operationId);

        // Show notification
        showSuccessRef.current(deleteMessage); // Use showSuccess (green) for successful deletion

        // Clear operation ID immediately after showing notification to prevent duplicates
        deleteOperationIdRef.current = null;

        // Clean up old notification IDs after a delay to prevent memory leaks
        setTimeout(() => {
          deleteNotificationsShown.delete(operationId);
        }, 5000);

        // Navigate back to list after a short delay to ensure notification is visible
        // Move navigation here to ensure it happens only once
        setTimeout(() => {
          navigate('/settings/voice-prompts/music-hold');
        }, 100);
      } else if (deleteNotificationsShown.has(operationId)) {
        // If notification was already shown, just navigate without showing again
        setTimeout(() => {
          navigate('/settings/voice-prompts/music-hold');
        }, 100);
      }
    } catch (error: any) {
      // Clear operation ID on error to allow retry
      if (deleteOperationIdRef.current === operationId) {
        deleteOperationIdRef.current = null;
        showErrorRef.current(error.message || t('mohSources.messages.deleteError') || 'Failed to delete MOH source');
        // Don't close modal on error - user might want to retry
        setShowDeleteModal(true);
      }
    } finally {
      // Reset refs in finally to ensure they're always cleared
      setIsDeleting(false);
      isDeletingRef.current = false;
      // Don't clear operationIdRef here - it's cleared in success/error handlers
    }
  }, [id, t, navigate]);

  // Handle back navigation
  const handleBack = useCallback(() => {
    navigate('/settings/voice-prompts/music-hold');
  }, [navigate]);

  if (isLoading) {
    return (
      <div className="max-w-[1920px] mx-auto">
        <div className="p-6 pt-4">
          <ModuleFormHeader
            icon={<Music className="h-5 w-5 text-primary" />}
            title={isEditMode ? (t('mohSources.actions.edit') || 'Edit MOH Source') : (t('mohSources.actions.create') || 'Create MOH Source')}
            backButtonText={t('common:list') || 'Back to List'}
            onBack={handleBack}
          />
          <div className="flex items-center justify-center py-12">
            <div className="text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
              <p className="text-sm text-muted-foreground">{t('common:loading') || 'Loading...'}</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <ModuleFormHeader
        icon={<Music className="h-5 w-5 text-primary" />}
        title={isEditMode ? `${t('mohSources.actions.edit') || 'Edit MOH Source'} - ${formData.name || ''}` : (t('mohSources.actions.create') || 'Create MOH Source')}
        backButtonText={t('common:list') || 'Back to List'}
        onBack={handleBack}
      />

      <div className="mt-8">
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Basic Information */}
          <FormBox
            title={t('mohSources.sections.basic') || 'Basic Information'}
            description={t('mohSources.sections.basicDescription') || 'General settings for the MOH source'}
            icon={<Music className="h-5 w-5" />}
          >
            {/* 4-Column Grid Layout: Labels and Inputs on same line */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 items-center">
              {/* Row 1: Name* | Input | Description | Input */}
              <SimpleTooltip
                content={t('mohSources.tooltips.name') || t('mohSources.fields.name')}
                position="top"
                maxWidth="300px"
              >
                <Label htmlFor="name" className="text-sm font-medium text-foreground">
                  {t('mohSources.fields.name') || 'Name'} <span className="text-red-500">*</span>
                </Label>
              </SimpleTooltip>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                placeholder={t('mohSources.fields.namePlaceholder') || 'e.g., Sales Jingle'}
                required
                disabled={isDefault}
              />
              <SimpleTooltip
                content={t('mohSources.tooltips.description') || t('mohSources.fields.description')}
                position="top"
                maxWidth="300px"
              >
                <Label htmlFor="description" className="text-sm font-medium text-foreground">
                  {t('mohSources.fields.description') || 'Description'}
                </Label>
              </SimpleTooltip>
              <Input
                id="description"
                value={formData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                placeholder={t('mohSources.fields.descriptionPlaceholder') || 'Optional description'}
              />

              {/* Row 2: Source Type | Select | Enabled | Toggle */}
              <SimpleTooltip
                content={t('mohSources.tooltips.sourceType') || t('mohSources.fields.sourceType')}
                position="top"
                maxWidth="300px"
              >
                <Label htmlFor="source_type" className="text-sm font-medium text-foreground">
                  {t('mohSources.fields.sourceType') || 'Source Type'} <span className="text-red-500">*</span>
                </Label>
              </SimpleTooltip>
              <Select
                value={formData.source_type}
                onValueChange={(value) => handleInputChange('source_type', value)}
                options={[
                  { value: 'file', label: t('mohSources.sourceTypes.file') || 'File' },
                  { value: 'stream', label: t('mohSources.sourceTypes.stream') || 'Stream' }
                ]}
              />
              <SimpleTooltip
                content={t('mohSources.tooltips.enabled') || t('mohSources.fields.enabled')}
                position="top"
                maxWidth="300px"
              >
                <Label htmlFor="enabled" className="text-sm font-medium text-foreground">
                  {t('mohSources.fields.enabled') || 'Enabled'}
                </Label>
              </SimpleTooltip>
              <div className="flex justify-start">
                <Toggle
                  id="enabled"
                  value={formData.enabled}
                  onChange={(value) => handleInputChange('enabled', value)}
                />
              </div>
            </div>

            {/* Class Name - Hidden, auto-generated from name */}
            <input
              type="hidden"
              id="class_name"
              value={formData.class_name}
            />
          </FormBox>

          <MOHSourceConfig
            formData={formData}
            handleInputChange={handleInputChange}
            fileUpload={fileUpload}
            audioPreview={audioPreview}
            handleDragEnd={handleDragEnd}
            handleFileInputChange={handleFileInputChange}
            handleChimeFileInputChange={handleChimeFileInputChange}
            handleTestStream={handleTestStream}
            handleRemoveFile={handleRemoveFile}
            sensors={sensors}
            id={id}
            activeFileTab={activeFileTab}
            setActiveFileTab={setActiveFileTab}
            showError={showError}
            t={t}
          />
        </form>
      </div>

      <FixedActionBar
        onSaveWithAction={handleSaveWithAction}
        onCancel={handleBack}
        onDelete={isEditMode && !isDefault ? handleDelete : undefined}
        saveText={t('common:save') || 'Save'}
        cancelText={t('common:cancel') || 'Cancel'}
        deleteText={t('common:delete') || 'Delete'}
        isSaving={isSaving}
        isEditMode={isEditMode}
        hasChanges={hasChanges}
        saveDisabled={isSaving}
        deleteDisabled={isDeleting}
        useSaveDropdown={true}
        saveDropdownStorageKey="mohSources:saveActionPreference"
      />

      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => {
          if (!isDeleting) {
            setShowDeleteModal(false);
          }
        }}
        onConfirm={() => {
          // Wrap in immediate check to prevent double execution
          if (isDeleting || isDeletingRef.current) {
            return;
          }
          handleDeleteConfirm();
        }}
        title={t('mohSources.messages.deleteTitle') || 'Delete MOH Source'}
        message={t('mohSources.messages.confirmDelete', { name: formData.name }) || `Are you sure you want to delete "${formData.name}"? This action cannot be undone.`}
        confirmText={t('common:delete') || 'Delete'}
        cancelText={t('common:cancel') || 'Cancel'}
        type="danger"
        isLoading={isDeleting}
      />
    </div>
  );
}


